import React, { useState, useEffect } from 'react';
import './FilterBar.css';

const EMPTY = new Set();

// Reusable filter bar matching the Governing Documents style: a top "All"
// button plus one checkbox dropdown per dimension. Controlled — the parent owns
// the selection (an object of Sets keyed by dimension.key) and the handlers.
//
// Props:
//   allCount       number shown on the "All" button and each dropdown's "All" row
//   allLabel       label for the reset option (default "All")
//   dimensions     [{ key, label, values: [{ value, label, count }] }]
//   selected       { [key]: Set(values) }
//   onToggleValue  (key, value) => void
//   onClearDimension (key) => void   — selects "All" for that dimension
//   onClearAll     () => void        — resets every dimension
//   trailing       optional nodes rendered after the dropdowns (e.g. Expand All)
function FilterBar({ allCount, allLabel = 'All', dimensions, selected, onToggleValue, onClearDimension, onClearAll, trailing }) {
  const [openKey, setOpenKey] = useState(null);

  // Close the open dropdown on Escape or any click that is not on a dropdown
  // trigger or inside an open panel (so the "All" button and blank space dismiss it).
  useEffect(() => {
    if (!openKey) return undefined;
    const onDown = (e) => {
      if (e.target.closest('.filter-dropdown-trigger') || e.target.closest('.filter-dropdown-panel')) return;
      setOpenKey(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenKey(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openKey]);

  const anySelected = dimensions.some(d => (selected[d.key] && selected[d.key].size > 0));

  return (
    <div className="filter-bar">
      <button
        className={`filter-all-btn ${!anySelected ? 'active' : ''}`}
        onClick={() => { onClearAll(); setOpenKey(null); }}
        aria-pressed={!anySelected}
      >
        {allLabel} ({allCount})
      </button>

      {dimensions.map(dim => {
        const sel = selected[dim.key] || EMPTY;
        const count = sel.size;
        const open = openKey === dim.key;
        return (
          <div className="filter-dropdown" key={dim.key}>
            <button
              className={`filter-dropdown-trigger ${count > 0 ? 'has-selection' : ''} ${open ? 'open' : ''}`}
              onClick={() => setOpenKey(open ? null : dim.key)}
              aria-expanded={open}
              aria-haspopup="true"
            >
              {dim.label}{count > 0 ? ` (${count})` : ''}
              <span className="filter-caret" aria-hidden="true"></span>
            </button>
            {open && (
              <div className="filter-dropdown-panel" role="group" aria-label={`Filter by ${dim.label}`}>
                <label className="filter-check">
                  <input type="checkbox" checked={count === 0} onChange={() => onClearDimension(dim.key)} />
                  <span className="filter-check-text">{allLabel} ({allCount})</span>
                </label>
                {dim.values.map(v => (
                  <label className="filter-check" key={String(v.value)}>
                    <input type="checkbox" checked={sel.has(v.value)} onChange={() => onToggleValue(dim.key, v.value)} />
                    <span className="filter-check-text">{v.label} ({v.count})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {trailing}
    </div>
  );
}

// Result-count + active-filter summary line, styled like the Governing
// Documents count line. Shows "{count} {noun}" always, plus an italic
// description of the active selections (grouped "or" within a dimension,
// "+" across dimensions) and a clear button when anything is selected.
export function FilterSummary({ count, noun, nounPlural, dimensions, selected, onClearAll }) {
  const groups = dimensions
    .filter(dim => selected[dim.key] && selected[dim.key].size > 0)
    .map(dim => dim.values.filter(v => selected[dim.key].has(v.value)).map(v => v.label));

  return (
    <div className="filter-summary" aria-live="polite">
      {count} {count === 1 ? noun : nounPlural}
      {groups.length > 0 && (
        <span className="filter-summary-detail">
          {' filtered by '}
          {groups.map((g, gi) => (
            <span key={gi}>
              {gi > 0 ? ' + ' : ''}
              {g.map((l, li) => (
                <span key={li}>{li > 0 ? ' or ' : ''}&ldquo;{l}&rdquo;</span>
              ))}
            </span>
          ))}
          {' '}
          <button className="filter-summary-clear" onClick={onClearAll} aria-label="Clear all filters">×</button>
        </span>
      )}
    </div>
  );
}

export default FilterBar;
